// /src/core/services/audio.service.ts
import * as Tone from 'tone';

/**
 * @file src/core/services/audio.service.ts
 * @description Servicio para manejar la reproducción de efectos de sonido en la aplicación.
 */

let synth: Tone.PolySynth | null = null;

/**
 * Inicializa el sintetizador de audio.
 * Se debe llamar después de la primera interacción del usuario.
 */
function initAudio(): void {
    if (synth) return;

    // Se usa un sintetizador 'sine' para un tono puro y suave.
    synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: 'sine' },
        envelope: {
            attack: 0.005,
            decay: 0.1,
            sustain: 0.3,
            release: 1.8 // Un release más largo para que el sonido se desvanezca suavemente.
        }
    });

    // Se añade un control de volumen para evitar distorsión (clipping).
    // Reduce la ganancia en -8 decibelios (dB) para dar "espacio" a las notas.
    const volumeNode = new Tone.Volume(-8);

    // Se añade reverberación para dar una sensación de espacio y mayor duración.
    const reverb = new Tone.Reverb({
        decay: 3,  // Duración del eco.
        wet: 0.3   // Cantidad de efecto (30%).
    }).toDestination();

    // Conexión en cadena: el sonido del sintetizador pasa por el control de volumen,
    // luego por la reverberación y finalmente a la salida de audio.
    // Flujo: synth -> volumeNode -> reverb -> destination
    synth.chain(volumeNode, reverb);
    
    console.log("Sintetizador de audio con efectos (volumen y reverb) inicializado.");
}

/**
 * Prepara el contexto de audio. Debe ser llamado por una interacción del usuario.
 * @returns {Promise<void>}
 */
export async function setupAudioContext(): Promise<void> {
    if (Tone.context.state !== 'running') {
        await Tone.start();
        console.log("AudioContext iniciado por interacción del usuario.");
    }
    initAudio();
}

/**
 * Reproduce una secuencia de notas para simular un sonido de "éxito".
 * Este sonido es similar al de una caja registradora o una notificación positiva.
 */
export function playSuccessSound(): void {
    if (!synth) {
        console.warn("El sintetizador de audio no está listo. Intenta de nuevo.");
        // Opcionalmente, intentar inicializarlo de nuevo si el contexto ya está activo
        if (Tone.context.state === 'running') {
            initAudio();
        } else {
            return;
        }
    }

    // Asegurarse de que el sintetizador esté listo antes de usarlo.
    if (!synth) return;

    const now = Tone.now() as number;

    // El arpegio original, ahora con un sonido más largo, con eco y nuevos tiempos.
    synth.triggerAttackRelease('C5', '8n', now);
    synth.triggerAttackRelease('E5', '8n', now + 0.15);
    synth.triggerAttackRelease('G5', '8n', now + 0.3);
}

let lockAudioInstance: HTMLAudioElement | null = null;

/**
 * Reproduce un sonido mecánico realista de apertura de candado ("clic metálico").
 * Utiliza una grabación de audio real pregrabada (MP3) alojada en /assets/audio/candado.mp3
 * para garantizar máxima autenticidad, eliminando cualquier sonido sintético.
 */
export function playLockOpenSound(): void {
    try {
        if (!lockAudioInstance) {
            lockAudioInstance = new Audio('/assets/audio/candado.mp3');
            lockAudioInstance.volume = 0.85;
        }
        lockAudioInstance.currentTime = 0;
        const playPromise = lockAudioInstance.play();
        if (playPromise !== undefined) {
            playPromise.catch((err) => {
                console.warn("[AudioService] Reproducción de candado prevenida:", err);
            });
        }
    } catch (err) {
        console.warn("[AudioService] No se pudo reproducir sonido de candado:", err);
    }
}

